/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { rectangleWithBorderRadius } from '#utils/PRIVATE_visSVGUtils';
import { GanttViewportTaskLayoutObj } from './dataLayoutUtils';

/**
 * Draws a given task effect onto a given Canvas.
 * @param ctx The Canvas's 2D context.
 * @param effectLayout The task effect layout object.
 */
const drawTaskEffectRing = (
  ctx: CanvasRenderingContext2D,
  effectLayout: {
    x: number;
    y: number;
    width: number;
    height: number;
    borderRadius: string;
    stroke: string;
    strokeWidth: number;
    padding: number;
  }
) => {
  const { x, y, width, height, borderRadius, stroke, strokeWidth, padding } = effectLayout;
  const ringX = x - padding;
  const ringY = y - padding;
  const ringWidth = width + 2 * padding;
  const ringHeight = height + 2 * padding;
  const effectRingCmd = rectangleWithBorderRadius(
    ringX,
    ringY,
    ringWidth,
    ringHeight,
    borderRadius,
    Math.min(ringWidth, ringHeight),
    '0'
  );
  const effectRingPath = new Path2D(effectRingCmd);
  ctx.strokeStyle = stroke;
  ctx.lineWidth = strokeWidth;
  ctx.stroke(effectRingPath);
};

/**
 * Draws a given task effect shadow onto a given Canvas.
 * @param ctx The Canvas's 2D context.
 * @param shadow The shadow definition.
 */
const drawTaskEffectShadow = (
  ctx: CanvasRenderingContext2D,
  shadow: {
    shadowColor: string;
    shadowOffsetX: number;
    shadowOffsetY: number;
    shadowBlur: number;
  }
) => {
  ctx.shadowColor = shadow.shadowColor;
  ctx.shadowOffsetX = shadow.shadowOffsetX;
  ctx.shadowOffsetY = shadow.shadowOffsetY;
  ctx.shadowBlur = shadow.shadowBlur;
};

/**
 * Resets any shadow settings in the Canvas's context.
 * @param ctx The Canvas's 2D context.
 */
const resetShadow = (ctx: CanvasRenderingContext2D) => {
  ctx.shadowColor = 'transparent';
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.shadowBlur = 0;
};

/**
 * Draws a given task onto a given Canvas.
 * @param ctx The Canvas's 2D context.
 * @param taskLayoutObj The task layout object.
 * @param state The task's state.
 */
const drawTask = <K2>(
  ctx: CanvasRenderingContext2D,
  taskLayoutObj: GanttViewportTaskLayoutObj<K2>,
  state?: 'selected' | 'hovered' | 'focused'
) => {
  const drawShadow = state === 'hovered';
  const { x, y, width, height, borderRadius, fill, stroke, strokeWidth } = taskLayoutObj;

  const roundRectCmd = rectangleWithBorderRadius(
    x,
    y,
    width,
    height,
    borderRadius,
    Math.min(width, height),
    '0'
  );
  const roundRectPath = new Path2D(roundRectCmd);

  // Draw white task backdrop
  if (drawShadow) drawTaskEffectShadow(ctx, taskLayoutObj.hoverEffect.shadow);
  ctx.fillStyle = '#ffffff';
  ctx.fill(roundRectPath);
  if (drawShadow) resetShadow(ctx);

  // Draw task
  ctx.strokeStyle = stroke;
  ctx.fillStyle = fill;
  ctx.lineWidth = strokeWidth;
  ctx.fill(roundRectPath);
  ctx.stroke(roundRectPath);

  switch (state) {
    case 'selected': {
      drawTaskEffectRing(ctx, {
        x,
        y,
        width,
        height,
        borderRadius,
        ...taskLayoutObj.selectionEffect
      });
      break;
    }
    case 'hovered': {
      drawTaskEffectRing(ctx, { x, y, width, height, borderRadius, ...taskLayoutObj.hoverEffect });
      break;
    }
    case 'focused': {
      drawTaskEffectRing(ctx, { x, y, width, height, borderRadius, ...taskLayoutObj.focusEffect });
      break;
    }
    case undefined: {
      // No effect
      break;
    }
    default: {
      const _exhaustiveCheck: never = state;
      return _exhaustiveCheck;
    }
  }
};

export { drawTask };
